import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Checkbox, Chip, CircularProgress, ListItemButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import _ from 'lodash';
import moment from 'moment';
import orderServices from 'app/services/order-services';
import StateBadge from 'app/widgets/Shared/StateBadge';
import { encodeParams } from 'app/utils/appHelpers';

// Lista de pedidos con checkboxes del modal del instructivo: se elige varios y se agregan al
// viaje de una vez.
//
// Muestra todos los pedidos menos los finalizados y cancelados, que es lo que el alta acepta:
// el filtro vive en el backend, en `only_planificables`.
//
// Los que estan en OTRO viaje activo se ofrecen igual, con un chip que dice en cual: traerlos
// aca los saca de ese viaje, y el alta pide confirmacion antes. Por eso hace falta `tripId`, que
// es lo que le permite al backend distinguir "otro viaje" del viaje que se esta editando.
//
// El tamano de pagina lo fija el backend (`orders#index` pagina de a 10) y el orden tambien
// (`ship_date DESC`), asi que la primera pagina ya son los mas recientes y aca no se reordena
// ni se pide un `per_page` propio.

const OrdersPicker = ({ tripId, selectedIds = [], onToggle, disabled = false }) => {
    const [search, setSearch] = useState('');
    const [orders, setOrders] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [loading, setLoading] = useState(false);

    const sentinelRef = useRef(null);
    // Cada busqueda incrementa el token; las respuestas de una busqueda vieja se descartan.
    // Sin esto, escribir rapido dejaba pintados los resultados de la consulta anterior.
    const requestToken = useRef(0);
    // El observer puede disparar dos veces antes de que `loading` se refleje en el render, y
    // sin esta guarda la misma pagina entraba dos veces y la lista mostraba filas repetidas.
    const inFlight = useRef(false);

    const fetchOrders = useCallback((page, term) => {
        if (inFlight.current) return Promise.resolve();

        const token = requestToken.current;
        inFlight.current = true;
        setLoading(true);

        const params = encodeParams(_.compact([
            "only_planificables=true",
            "include=locatable,locatable.location",
            // `with_delivery_state` es lo que trae `current_trip_code`, o sea el chip del viaje.
            "with_delivery_state=true",
            tripId ? `trip_id=${tripId}` : null,
            `page=${page}`,
            term ? `q[code_or_locatable_of_Customer_type_name_or_locatable_of_Place_type_name_cont]=${term}` : null
        ]).join('&'));

        return orderServices.getOrders(`?${params}`)
            .then((data) => {
                if (token !== requestToken.current) return;

                // `uniqBy` es red de seguridad: el backend ya no repite pedidos, pero entre una
                // pagina y la siguiente uno puede correrse de lugar y llegar dos veces. Dos filas
                // con el mismo id rompen las keys de React y descuadran el contador.
                setOrders((previous) => _.uniqBy(page === 1 ? data.data : [...previous, ...data.data], 'id'));
                setNextPage(data.meta?.next_page || null);
            })
            .finally(() => {
                inFlight.current = false;
                if (token !== requestToken.current) return;
                setLoading(false);
            });
    }, [tripId]);

    // Primera pagina y recarga al cambiar el filtro. El debounce es el mismo de 500 ms que ya
    // usaba el buscador de un solo pedido.
    useEffect(() => {
        const run = _.debounce(() => {
            requestToken.current += 1;
            inFlight.current = false;
            setOrders([]);
            setNextPage(null);
            fetchOrders(1, search);
        }, search ? 500 : 0);

        run();
        return () => run.cancel();
    }, [search, fetchOrders]);

    // Scroll infinito: cuando el pie de la lista entra en pantalla se pide la pagina siguiente.
    // El observer se recrea cuando cambia `nextPage` para no quedarse con un callback viejo.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !nextPage || loading) return undefined;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                fetchOrders(nextPage, search);
            }
        }, { root: sentinel.parentElement, rootMargin: '80px' });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [nextPage, loading, search, fetchOrders]);

    const renderOrder = (order) => {
        const checked = _.includes(selectedIds, order.id);

        return (
            <ListItemButton
                key={order.id}
                dense
                disabled={disabled}
                onClick={() => onToggle(order)}
                sx={{ py: 0.5, pl: 1.5, pr: 2, gap: 1.5 }}
            >
                <Checkbox size="small" checked={checked} tabIndex={-1} disableRipple sx={{ p: 0 }} />
                <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={0}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body1">{order.code}</Typography>
                        <Typography variant="body2" noWrap>{order.locatable?.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {order.ship_date && (
                            <Typography variant="caption" color="text.secondary">
                                {moment(order.ship_date).format('DD MMM YYYY')}
                            </Typography>
                        )}
                        {/* Ya esta en otro viaje: se puede traer, pero conviene verlo antes de
                            marcarlo y no recien en el dialogo de confirmacion. */}
                        {order.current_trip_code && (
                            <Tooltip arrow title="Está en otro viaje. Si lo agregas acá, se elimina de ese viaje.">
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={`Viaje N°${order.current_trip_code}`}
                                    sx={{height: 18, fontSize: 10}}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>
                <StateBadge state={order.state} stateName={order.state_name} withIcon={false} />
            </ListItemButton>
        );
    };

    const emptyMessage = loading ? null : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {search ? 'No hay pedidos que coincidan' : 'No hay pedidos disponibles para agregar'}
        </Typography>
    );

    return (
        <Stack spacing={1}>
            <TextField
                fullWidth
                size="small"
                label="Buscar pedido"
                value={search}
                disabled={disabled}
                onChange={(e) => setSearch(e.target.value)}
            />
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {orders.length === 0 && emptyMessage}
                {_.map(orders, renderOrder)}
                {/* Pie de la lista: dispara la carga y muestra el spinner mientras llega. */}
                <Stack ref={sentinelRef} alignItems="center" sx={{ py: loading || nextPage ? 1 : 0 }}>
                    {loading && <CircularProgress size={20} />}
                </Stack>
            </Box>
        </Stack>
    );
};

OrdersPicker.propTypes = {
    // El viaje que se esta editando: define que cuenta como "otro viaje".
    tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedIds: PropTypes.array,
    // Recibe el pedido completo: el padre necesita el codigo para el modal de confirmacion.
    onToggle: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default OrdersPicker;
